import { IRxHttp, ModelState, isModelState } from './IHttp';
import { RemoteData } from './RemoteData';
import { getUrl } from './common-functions';
import { Observable, Observer, SchedulerLike, throwError, timer, of } from 'rxjs';
import { observeOn, retryWhen, delay, scan, takeWhile, mergeMap, catchError, tap } from 'rxjs/operators';

export type XhrHttpRetryOptions = {    
    attempts: number
    delayMs: number
    excludedStatusCodes: number[]
    onHttpUnauthorized?: () => any
}

export class XhrHttp implements IRxHttp {

    retryOptions: XhrHttpRetryOptions = {
        attempts: 4,
        delayMs: 1000,
        excludedStatusCodes: [400, 401, 404]        
    };

    constructor(private baseUrl: string, private scheduler?: SchedulerLike, options?: Partial<XhrHttpRetryOptions>) {     
        const defaultOptions: XhrHttpRetryOptions = {            
            attempts: 4,
            delayMs: 1000,
            excludedStatusCodes: [400, 401, 404]            
        };
        if (!options) {
            options = { };
        }
        this.retryOptions = { ...this.retryOptions, ...options };
    }   

    private getUrl(path: string, queryParams?: any) {
        return getUrl(this.baseUrl, path, queryParams);
    }

    private isSuccessStatusCode(httpStatusCode: number): boolean {
        return httpStatusCode === 200 // OK
            || httpStatusCode === 201 // Created
            || httpStatusCode === 202 // Accepted
            || httpStatusCode === 204; // No Content
    }

    private isValidationErrorStatusCode(httpStatusCode: number, httpResponseText: any): boolean {        
        if (httpStatusCode === 400) {   
            try {                
                const modelState = this.parseData<ModelState>(httpResponseText);                
                if (isModelState(modelState)) {                    
                    return true;
                }
            }
            catch { }
        }       
        return false;
    }

    private parseData<T>(responseText: string): T | null {
        return responseText === ''
            ? null
            : <T>JSON.parse(responseText);
    }
    
    get<T>(path: string, queryParams?: any): Promise<T> {
        return this
            .getObs<T>(path, queryParams)
            .toPromise();
    }

    post<T>(path: string, data: any): Promise<T> {
        return this
            .postObs<T>(path, data)
            .toPromise();
    }

    private getError(xhr: XMLHttpRequest) {
        return {
            status: xhr.status,
            message: xhr.responseText || xhr.statusText
        };
    }

    getObs<T>(path: string, queryParams?: any): Observable<T> {
        const obs: Observable<T> = Observable.create(observer => {
            const url = this.getUrl(path, queryParams);
            const xhr = new XMLHttpRequest();

            const onLoad = () => {
                if (this.isSuccessStatusCode(xhr.status)) {
                    const data = this.parseData<T>(xhr.responseText);
                    observer.next(data);
                    observer.complete();
                } else {
                    observer.error(this.getError(xhr));
                }
            };

            const onError = e => observer.error(this.getError(xhr));

            xhr.addEventListener('load', onLoad);
            xhr.addEventListener('error', onError);

            xhr.open('GET', url);            
            xhr.send();

            return () => {
                xhr.removeEventListener('load', onLoad);
                xhr.removeEventListener('error', onError);
                xhr.abort();
            };
        }).pipe(
            retryWhen(this.retryStrategy(this.retryOptions))
        );
        return (this.scheduler)
            ? obs.pipe(observeOn(this.scheduler))
            : obs;
    }

    postObs<T>(path: string, data: any): Observable<T> {
        const obs: Observable<T> = Observable.create(observer => {
            const url = this.getUrl(path);
            const xhr = new XMLHttpRequest();

            const onLoad = () => {
                if (this.isSuccessStatusCode(xhr.status)) {
                    const data = this.parseData<T>(xhr.responseText);
                    observer.next(data);
                    observer.complete();
                } else if (this.isValidationErrorStatusCode(xhr.status, xhr.responseText)) {                    
                    const modelState = this.parseData<ModelState>(xhr.responseText);
                    observer.error(modelState);
                } else {                                        
                    observer.error(this.getError(xhr));
                }
            };

            const onError = e => observer.error(this.getError(xhr));                              

            xhr.addEventListener('load', onLoad);
            xhr.addEventListener('error', onError);

            xhr.open('POST', url, true);
            xhr.setRequestHeader('Content-Type', 'application/json');
            xhr.send(JSON.stringify(data));

            return () => {
                xhr.removeEventListener('load', onLoad);
                xhr.removeEventListener('error', onError);
                xhr.abort();
            };
        })
        //.pipe(
        //    retryWhen(this.retryStrategy(this.retryOptions))            
        //)
        ;
        return (this.scheduler)
            ? obs.pipe(observeOn(this.scheduler))
            : obs;
    }
    private retryStrategy(options: XhrHttpRetryOptions): (any) => Observable<any> {
        return function (errors: Observable<any>): Observable<any> {
            return errors.pipe(
                mergeMap((error, i) => {
                    const retryAttempt = i + 1;                                        
                    if (isModelState(error)) {
                        return throwError(error);
                    }                    
                    if (error.status === 401
                        && options.excludedStatusCodes.find(e => e === 401)) {
                        //if (options.onHttpUnauthorized) {
                        //    options.onHttpUnauthorized();
                        //}
                        return throwError({
                            errorMessage: 'Έχετε αποσυνδεθεί από την εφαρμογή!',
                            isUnauthorized: true
                        });                                                    
                    }
                    if (
                        retryAttempt > options.attempts ||                        
                        options.excludedStatusCodes.find(e => e === error.status)
                    ) {                        
                        return throwError(error.message);
                    }
                    return timer(retryAttempt * options.delayMs);
                })
            );            
        };
    }    
        

    getRemoteData<T>(path: string, queryParams?: any): Observable<RemoteData<T>> {
        const obs: Observable<RemoteData<T>> = Observable.create((observer: Observer<RemoteData<T>>) => {
            observer.next({ kind: 'notFetched' });

            const url = this.getUrl(path, queryParams);

            const xhr = new XMLHttpRequest();

            const onLoad = () => {
                if (this.isSuccessStatusCode(xhr.status)) {
                    const data = this.parseData<T>(xhr.responseText);
                    observer.next({ kind: 'ok', data: data });
                    observer.complete();
                } else {
                    observer.next({ kind: 'error', error: xhr.responseText });                    
                }
            };

            const onError = e => observer.error(this.getError(xhr));

            xhr.addEventListener('load', onLoad);
            xhr.addEventListener('error', onError);

            xhr.open('GET', url);            

            observer.next({ kind: 'loading' });
            xhr.send();

            return () => {
                if (!observer.closed) {
                    observer.next({ kind: 'notFetched' });
                }
                xhr.removeEventListener('load', onLoad);
                xhr.removeEventListener('error', onError);
                xhr.abort();
            };
        }).pipe(
            retryWhen(this.retryStrategy(this.retryOptions))
        );
        return (this.scheduler)
            ? obs.pipe(observeOn(this.scheduler))
            : obs;
    }

    postRemoteData<T>(path: string, data: any): Observable<RemoteData<T>> {
        const obs: Observable<RemoteData<T>> = Observable.create((observer: Observer<RemoteData<T>>) => {
            observer.next({ kind: 'notFetched' });

            const url = this.getUrl(path);
            const xhr = new XMLHttpRequest();

            const onLoad = () => {
                if (this.isSuccessStatusCode(xhr.status)) {
                    const data = this.parseData<T>(xhr.responseText);
                    observer.next({ kind: 'ok', data: data });
                    observer.complete();
                } else {
                    observer.next({ kind: 'error', error: xhr.responseText });
                    //observer.error(xhr.responseText);
                }
            };

            const onError = e => observer.error(xhr.responseText);

            xhr.addEventListener('load', onLoad);
            xhr.addEventListener('error', onError);

            xhr.open('POST', url, true);            
            xhr.setRequestHeader('Content-Type', 'application/json');

            observer.next({ kind: 'loading' });
            xhr.send(JSON.stringify(data));

            return () => {
                if (!observer.closed) {
                    observer.next({ kind: 'notFetched' });
                }
                xhr.removeEventListener('load', onLoad);
                xhr.removeEventListener('error', onError);
                xhr.abort();
            };
        })
        //.pipe(
        //    retryWhen(this.retryStrategy(this.retryOptions))
        //)
        ;

        return (this.scheduler)
            ? obs.pipe(observeOn(this.scheduler))
            : obs;
    }

}